/**
 * Adapt kryspace Drive personal-layer payloads that diverge from the seq-60 FE contract
 * (substrate alias, engagement_lane, leaf-as-subdomain, domain-as-laneIds).
 *
 * Contract decision (2026-07-15, ai-act-drive-lane-contract-decision):
 * - Short-term (B): FE adapter accepts live shape — this module + driveSectionLayout nesting.
 * - Long-term (A): kryspace emitters should emit typed overview lanes only; leaves in flowArtifacts.
 */

import type { DriveDomainSection, DriveHiFiModel, DriveLaneMode, DriveSubdomainNode } from './types';
import { scopeIdsEqual } from '../../../utils/scopeIdPlane';

/** Manifest may emit `substrate`; FE canonical mode is `ledger_substrate`. */
export function normalizeDriveLaneMode(
  mode: string | undefined
): DriveLaneMode | string | undefined {
  if (!mode) return mode;
  if (mode === 'substrate') return 'ledger_substrate';
  return mode;
}

export function isSubstrateSection(section: DriveDomainSection): boolean {
  const mode = normalizeDriveLaneMode(section.laneMode);
  if (mode === 'ledger_substrate') return true;
  if (section.isSubstrate === true) return true;
  const laneIds = section.laneIds ?? [];
  return laneIds.length === 1 && laneIds[0] === section.id;
}

export function normalizeDriveDomainSection(section: DriveDomainSection): DriveDomainSection {
  return {
    ...section,
    laneMode: normalizeDriveLaneMode(section.laneMode),
    isSubstrate: section.isSubstrate === true || isSubstrateSection(section),
  };
}

export function normalizeDriveSubdomain(sub: DriveSubdomainNode): DriveSubdomainNode {
  return {
    ...sub,
    laneMode: normalizeDriveLaneMode(sub.laneMode),
  };
}

/** Last meaningful segment for prefix matching (`ca-krysalis-contact` → `contact`). */
export function typedLaneStem(typedId: string): string {
  const parts = typedId.split('-').filter(Boolean);
  if (parts.length === 0) return typedId.toLowerCase();
  // Prefer trailing semantic tokens (contact, situation, deal, avatar, …).
  return (parts[parts.length - 1] ?? typedId).toLowerCase();
}

function pickTypedParentForOrphan(
  orphan: DriveSubdomainNode,
  typedIds: string[],
  byId: Map<string, DriveSubdomainNode>
): string | undefined {
  if (!typedIds.length) return undefined;

  const idMatch = typedIds.find(
    (tid) => orphan.id === tid || orphan.id.startsWith(`${tid}-`) || orphan.id.includes(tid)
  );
  if (idMatch) return idMatch;

  const orphanId = orphan.id.toLowerCase();
  const stemMatch = typedIds.find((tid) => {
    const stem = typedLaneStem(tid);
    if (stem.length < 3) return false;
    return orphanId.includes(stem) || orphanId.includes(`-${stem}-`);
  });
  if (stemMatch) return stemMatch;

  const name = orphan.name.toLowerCase();
  const nameMatch = typedIds.find((tid) => {
    const parent = byId.get(tid);
    const parentName = parent?.name?.toLowerCase() ?? '';
    return parentName.length > 2 && (name.includes(parentName) || parentName.includes(name));
  });
  if (nameMatch) return nameMatch;

  return undefined;
}

function attachOrphanToParent(
  orphan: DriveSubdomainNode,
  parentId: string,
  byId: Map<string, DriveSubdomainNode>,
  nestedLeafIds: Set<string>
): void {
  const parent = byId.get(parentId);
  if (!parent) return;
  const grandchildren = parent.grandchildren ?? [];
  if (!grandchildren.some((g) => scopeIdsEqual(g.id, orphan.id))) {
    parent.grandchildren = [...grandchildren, { id: orphan.id, name: orphan.name }];
  }
  nestedLeafIds.add(orphan.id);
}

/**
 * Nest leaf subdomains (same domainSectionId, not listed in section.laneIds) under typed
 * parent lanes as grandchildren so token aggregation can credit the parent column.
 */
export function nestOrphanLeavesUnderTypedLanes(
  subdomains: DriveSubdomainNode[],
  sections: DriveDomainSection[] | undefined
): { subdomains: DriveSubdomainNode[]; nestedLeafIds: Set<string> } {
  const nestedLeafIds = new Set<string>();
  if (!sections?.length) {
    return { subdomains, nestedLeafIds };
  }

  const byId = new Map(subdomains.map((sub) => [sub.id, { ...sub }]));
  const working = subdomains.map((sub) => byId.get(sub.id)!);

  const allTypedIds = new Set<string>();
  for (const section of sections) {
    for (const id of section.laneIds ?? []) {
      if (id !== section.id && byId.has(id)) allTypedIds.add(id);
    }
  }

  for (const section of sections) {
    if (isSubstrateSection(section)) {
      // Substrate: leaves stay overview columns keyed by domainSectionId.
      continue;
    }

    const typedIds = (section.laneIds ?? []).filter((id) => id !== section.id && byId.has(id));
    if (!typedIds.length) continue;

    const typedSet = new Set(typedIds);
    const orphans = working.filter(
      (sub) =>
        !typedSet.has(sub.id) &&
        !nestedLeafIds.has(sub.id) &&
        sub.id !== section.id &&
        sub.laneMode !== 'product_tier' &&
        !sub.parentProductLaneId &&
        // Prefer same domainSectionId; also allow unsectioned leaves matched by stem.
        (sub.domainSectionId === section.id || !sub.domainSectionId)
    );

    for (const orphan of orphans) {
      const parentId = pickTypedParentForOrphan(orphan, typedIds, byId);
      if (!parentId) continue;
      // Unsectioned leaves must stem-match (avoid dumping unrelated orphans onto first cluster).
      if (!orphan.domainSectionId) {
        const stem = typedLaneStem(parentId);
        if (stem.length < 3 || !orphan.id.toLowerCase().includes(stem)) continue;
      }
      attachOrphanToParent(orphan, parentId, byId, nestedLeafIds);
    }
  }

  // Second pass: unsectioned leaves that stem-match any typed lane across sections.
  const leftover = working.filter(
    (sub) =>
      !allTypedIds.has(sub.id) &&
      !nestedLeafIds.has(sub.id) &&
      !sub.domainSectionId &&
      sub.laneMode !== 'product_tier' &&
      !sub.parentProductLaneId &&
      !sub.laneMode
  );
  const typedList = Array.from(allTypedIds);
  for (const orphan of leftover) {
    const parentId = pickTypedParentForOrphan(orphan, typedList, byId);
    if (!parentId) continue;
    const stem = typedLaneStem(parentId);
    if (stem.length < 3 || !orphan.id.toLowerCase().includes(stem)) continue;
    attachOrphanToParent(orphan, parentId, byId, nestedLeafIds);
  }

  return {
    subdomains: working.map((sub) => byId.get(sub.id) ?? sub),
    nestedLeafIds,
  };
}

/** Normalize laneMode aliases (`substrate` → `ledger_substrate`) on sections/subdomains. */
export function adaptDriveHiFiModelForLiveShape(model: DriveHiFiModel): DriveHiFiModel {
  const sections = (model.sections ?? []).map(normalizeDriveDomainSection);
  return {
    ...model,
    sections: sections.length ? sections : model.sections,
    subdomains: model.subdomains.map(normalizeDriveSubdomain),
  };
}
